import * as React from "react";
import { cn } from "../../lib/utils";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  helperText?: string;
  resize?: "none" | "both" | "horizontal" | "vertical";
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    { className, label, error, helperText, resize = "vertical", ...props },
    ref
  ) => {
    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={props.id}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}

        <textarea
          ref={ref}
          className={cn(
            // Base styles
            "flex w-full min-h-[110px] rounded-lg bg-gray-100 text-sm text-gray-900 placeholder:text-gray-400",
            "px-3 py-2 shadow-sm transition-all duration-150 ease-in-out",
            
            // Scrollbar customization
            "overflow-y-auto",

            // Resizing
            resize === "none" && "resize-none",
            resize === "both" && "resize",
            resize === "horizontal" && "resize-x",
            resize === "vertical" && "resize-y",

            // Borderless, subtle outline and focus
            "outline-none border border-transparent focus:border-gray-300 focus:ring-1 focus:ring-purple-500/40",

            // Hover effect
            "hover:bg-gray-50 focus:bg-white",

            // Disabled
            "disabled:opacity-50 disabled:cursor-not-allowed",

            // Error state
            error && "bg-red-50 text-red-800 placeholder:text-red-400 focus:ring-red-400",

            className
          )}
          {...props}
        />

        {helperText && !error && (
          <p className="mt-1 text-xs text-gray-500">{helperText}</p>
        )}
        {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";
